/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Form, Formik } from "formik";
import { Box, Button, Grid, Switch } from "@material-ui/core";
import {
  OrganizationSettingsData,
  OrganizationSettingsUpdateData,
} from "../../../../../models/organizationSettingsData";
import CompanyHeading from "../../../../theming/CompanyHeading";
import { setOrganizationSettings } from "../../../../../redux/organizationSettings/actions";
import Loader from "../../../../theming/loader/Loader";
import { AppState } from "../../../../../redux";
import OrganizationDiscountManagement from "../discounts/OrganizationDiscountManagement";

/**
 * OrganizationSettingsManagement component
 *
 * Main form to manage organization settings including:
 * - Skipping guardians
 * - Managing discount codes
 *
 * Fetches the organization settings on mount and updates on form submit.
 *
 * @component
 */
const OrganizationSettingsManagement: React.FC = () => {
  const { t } = useTranslation(["organization", "snackbars", "common"]);
  const dispatch = useDispatch();

  const organizationSettings = useSelector(
    (state: AppState) => state.organizationSettings
  );

  /**
   * Handles toggling of form switch inputs.
   *
   * @param setFieldValue - Formik setFieldValue function
   * @param fieldName - The name of the field being updated
   * @returns Event handler for input change
   */
  const handleChange =
    (setFieldValue: any, fieldName: string) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValue(fieldName, event.target.checked);
    };

  /**
   * Handles form submission: prepares data and dispatches update action.
   *
   * @param values - Formik form values
   */
  const handleSubmit = async (values: OrganizationSettingsData) => {
    if (!organizationSettings?.organizationSettings?.id) return;

    const discountCodes = (
      organizationSettings.organizationSettings?.discounts ?? []
    ).map((d) => d.code);

    const updatedValues: OrganizationSettingsUpdateData = {
      skip_guardians: values.skip_guardians,
      discount_codes: discountCodes,
    };

    dispatch(
      setOrganizationSettings(
        organizationSettings.organizationSettings?.id,
        updatedValues
      )
    );
  };

  if (!organizationSettings?.isLoaded) {
    return <Loader />;
  }

  return (
    <>
      <CompanyHeading>{t("orgaSettings", { ns: "organization" })}</CompanyHeading>
      <Formik
        enableReinitialize
        initialValues={{
          id: organizationSettings?.organizationSettings?.id || "",
          skip_guardians:
            organizationSettings?.organizationSettings?.skip_guardians || false,
        }}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, values }) => (
          <Form>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Box>
                  <h3>{t("guardianSubheading", { ns: "organization" })}</h3>
                </Box>
                <Box className="confirm-condition-box">
                  <span>{t("skipGuardian", { ns: "organization" })}</span>
                  <Switch
                    color="primary"
                    checked={values.skip_guardians}
                    onChange={handleChange(setFieldValue, "skip_guardians")}
                    name="skip_guardians"
                    inputProps={{ "aria-label": "Skip Guardians checkbox" }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <OrganizationDiscountManagement />
              </Grid>
            </Grid>

            <Box style={{ marginTop: "1rem" }}>
              <Button type="submit" size="large" variant="contained" color="primary">
                {t("save", { ns: "common" })}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default OrganizationSettingsManagement;
